import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, AlertTitle, Button } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNotify, useRecordContext } from "react-admin";
import { useSearchParams } from "react-router-dom";
import { ResourceName, type Claimant } from "@/types";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useLaseredConditions } from "@/resources/policies/hooks/useLaseredConditions";
import { REVIEW_POLICY_QUERY_PARAM, REVIEW_QUERY_PARAM } from "@/resources/tasks/utils/taskRecordLink";
import { useSelectedPolicyData } from "@/resources/claimants/hooks/useCurrentPolicyData";
import { useDeductibleReviews } from "@/resources/claimants/hooks/useDeductibleReviews";
import { useRecomputeRefreshOrStandalone } from "@/resources/claimants/hooks/useRecomputeRefresh";
import { useOptionalPolicyContext } from "../PolicyContext";
import { DeductibleReviewDialog } from "./DeductibleReviewDialog";
import { ASD_NOT_HELD_TEXT, reviewHelperText } from "./reviewHelperText";
import { isAggregateSpecificPolicy } from "@/resources/clients/components/claims-summary/utils/aggregateSpecific";
import { LaserReviewIcon } from "@/components/ui/mui/LaserReviewIcon";
import { HoldStrip } from "@/components/common/HoldStrip";

/**
 * Claimant-page banner while a deductible review is open for the selected policy
 * (#7345 AC4, #3513 AC4): the primary call to action, with the same text as the
 * flag tooltip. Lives inside the claimant PolicyProvider.
 *
 * Deep link (#4243 task): `?review=open&policy=<id>` selects that policy, opens
 * the resolution dialog once and clears the parameters, so closing or a refresh
 * does not reopen it. Whether to open is decided from a fresh reviews fetch,
 * not the claimant record's pending flag, which may be a stale cached copy.
 */
/** Deep link without an open review (resolved, superseded, or never opened for this policy). */
const NO_OPEN_REVIEW = "There is no open deductible review for this policy.";

/**
 * `strip`: the in-card hold strip for the claimant position card (Feature #7659):
 * warning tint, title, the reason in one muted line, one quiet action. The hold
 * stands where the figures it blocks are, instead of a page-wide Alert.
 */
export const DeductibleReviewBanner = ({ variant = "alert" }: { variant?: "alert" | "strip" } = {}) => {
  const record = useRecordContext<Claimant>();
  const notify = useNotify();
  // Read-only users can open the review but not resolve it (PO decision 1).
  const { canEdit, canCreate, isLoading: permissionsLoading } = useResourcePermissions(ResourceName.CLAIMANTS);
  const canResolve = permissionsLoading || canEdit || canCreate;
  const policyCtx = useOptionalPolicyContext();
  const { policy, policyData, policyId } = useSelectedPolicyData();
  // ASD allocation is not held by the review (the claimant page has the policy).
  const asdPolicy = isAggregateSpecificPolicy(policy);
  const pending = !!policyData?.is_deductible_review_pending;
  const claimantId = record?.id ? String(record.id) : undefined;
  const [searchParams, setSearchParams] = useSearchParams();
  const wantsReview = searchParams.get(REVIEW_QUERY_PARAM) === "open";
  const wantedPolicy = searchParams.get(REVIEW_POLICY_QUERY_PARAM);
  const {
    openReview,
    isFetching: reviewsFetching,
    isFetchedAfterMount: reviewsFetched,
    error: reviewsError,
  } = useDeductibleReviews(claimantId, policyId, pending || wantsReview);
  const { conditions } = useLaseredConditions(policyId, claimantId, pending);
  const [open, setOpen] = useState(false);
  const deepLinkHandled = useRef(false);
  // Owned here, not by the dialog: the dialog unmounts on close (#7346 review).
  const { triggerRecomputeRefresh } = useRecomputeRefreshOrStandalone();
  const handleResolved = useCallback(() => triggerRecomputeRefresh({ notifyUser: false }), [triggerRecomputeRefresh]);

  useEffect(() => {
    if (!wantsReview) {
      deepLinkHandled.current = false;
      return;
    }
    if (deepLinkHandled.current || !record) return;
    if (wantedPolicy && policyCtx) {
      if (!policyCtx.policies.length) return; // policies still loading
      const known = policyCtx.policies.some((p) => String(p.id) === wantedPolicy);
      if (known && policyCtx.selectedPolicyId !== wantedPolicy) {
        policyCtx.setSelectedPolicyId(wantedPolicy);
        return; // re-run once the selection lands
      }
    }
    if (claimantId && policyId) {
      // Wait for a fetch made since this page opened; a cached list may be stale.
      if (reviewsFetching || !reviewsFetched) return;
      if (openReview) setOpen(true);
      else if (reviewsError) notify("Could not load the deductible review.", { type: "error" });
      else notify(NO_OPEN_REVIEW, { type: "info" });
    } else {
      notify(NO_OPEN_REVIEW, { type: "info" });
    }
    deepLinkHandled.current = true;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(REVIEW_QUERY_PARAM);
        next.delete(REVIEW_POLICY_QUERY_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [
    wantsReview,
    wantedPolicy,
    record,
    policyCtx,
    claimantId,
    policyId,
    reviewsFetching,
    reviewsFetched,
    reviewsError,
    openReview,
    notify,
    setSearchParams,
  ]);

  // Rendered apart from the banner: a deep link can open a review the cached
  // claimant record does not show as pending yet.
  const dialog =
    open && claimantId && policyId ? (
      <DeductibleReviewDialog
        open={open}
        onClose={() => setOpen(false)}
        onResolved={handleResolved}
        claimantId={claimantId}
        policyId={policyId}
        claimantName={record?.claimant_name}
        asdPolicy={asdPolicy}
      />
    ) : null;

  if (!pending || !claimantId || !policyId) return dialog;

  if (variant === "strip") {
    // The claim page's strip (StoplossClaimHoldBanner compact): same title, same shape.
    const reason = reviewHelperText(openReview, conditions).replace(/\s*Click to review\.?$/, "");
    return (
      <>
        <HoldStrip
          testId="deductible-review-banner"
          title={asdPolicy ? "Deductible review open" : "On hold: deductible review open"}
          reason={`${reason}${asdPolicy ? ` ${ASD_NOT_HELD_TEXT}` : ""}`}
          action={{
            label: canResolve ? "Resolve review" : "View review",
            onClick: () => setOpen(true),
            testId: "deductible-review-banner-action",
          }}
        />
        {dialog}
      </>
    );
  }

  return (
    <>
      <Alert
        severity="warning"
        role="status"
        icon={<LaserReviewIcon size={22} />}
        sx={{
          mb: 2,
          alignItems: "center",
          "& .MuiAlert-message": { fontSize: "0.8125rem", width: "100%" },
          "& .MuiAlert-action": { pt: 0, alignItems: "center" },
        }}
        action={
          <Button
            variant="contained"
            color="warning"
            disableElevation
            endIcon={<ChevronRightIcon />}
            onClick={() => setOpen(true)}
            data-testid="deductible-review-banner-action"
            sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
          >
            {canResolve ? "Resolve deductible review" : "View deductible review"}
          </Button>
        }
        data-testid="deductible-review-banner"
      >
        <AlertTitle sx={{ fontSize: "0.875rem" }}>
          {asdPolicy ? "Action needed: deductible review" : "Action needed: stop-loss claims on hold"}
        </AlertTitle>
        {reviewHelperText(openReview, conditions).replace(/ Click to review\.$/, "")}
        {asdPolicy && ` ${ASD_NOT_HELD_TEXT}`}
      </Alert>
      {dialog}
    </>
  );
};
